/**
 * Page scolarité : gestion des fenêtres de saisie des notes par les formateurs.
 * Permet d'ouvrir/fermer la possibilité aux formateurs de saisir des notes.
 */
import React, { useMemo, useState } from 'react';
import {
  Lock, Unlock, Plus, Edit2, Trash2, Calendar, CheckCircle2, X, Save, Loader2, Check,
  User as UserIcon, MessageSquare, XCircle,
} from 'lucide-react';
import {
  useListerFenetresQuery, useCreerFenetreMutation,
  useUpdateFenetreMutation, useDeleteFenetreMutation,
  useListerFormateursQuery,
  useMatieresDesFormateursQuery,
  useListerDemandesFenetreQuery, useTraiterDemandeFenetreMutation,
} from '../../services/formateurNotesApi';
import type { Fenetre, DemandeFenetre } from '../../services/formateurNotesApi';
import { useGetClassesQuery } from '../../services/polesOptionsApi';
import { useGetMatieresQuery } from '../../services/matieresApi';

export default function FenetresSaisie() {
  const { data } = useListerFenetresQuery();
  const [editing, setEditing] = useState<Fenetre | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [delMut] = useDeleteFenetreMutation();
  const [updMut] = useUpdateFenetreMutation();

  const { data: demandes } = useListerDemandesFenetreQuery('en_attente');
  const [traiterDemande, { isLoading: traitant }] = useTraiterDemandeFenetreMutation();
  const [detailDemande, setDetailDemande] = useState<DemandeFenetre | null>(null);
  const nbDemandesEnAttente = demandes?.nb_en_attente || 0;

  const handleApprouver = async (d: DemandeFenetre, duree = 7) => {
    try {
      await traiterDemande({ id: d.id, action: 'approuver', duree_jours: duree }).unwrap();
      setDetailDemande(null);
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };
  const handleRefuser = async (d: DemandeFenetre, reponse: string) => {
    if (!reponse.trim()) { alert('Merci de motiver le refus.'); return; }
    try {
      await traiterDemande({ id: d.id, action: 'refuser', reponse }).unwrap();
      setDetailDemande(null);
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  const handleToggle = async (f: Fenetre) => {
    try {
      await updMut({ id: f.id, data: { ouverte: !f.ouverte } }).unwrap();
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer cette fenêtre ?')) return;
    try { await delMut(id).unwrap(); }
    catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Lock size={24} /> Fenêtres de saisie
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Contrôlez quand les formateurs peuvent saisir les notes. Hors fenêtre ouverte, ils sont bloqués.
          </p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded inline-flex items-center gap-2">
          <Plus size={14} /> Nouvelle fenêtre
        </button>
      </div>

      {/* Bandeau demandes en attente (fix 2026-09-01) */}
      {nbDemandesEnAttente > 0 && (
        <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 border-b border-amber-200 dark:border-amber-800/50 flex items-center gap-2">
            <MessageSquare size={16} className="text-amber-600 dark:text-amber-400" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                {nbDemandesEnAttente} demande{nbDemandesEnAttente > 1 ? 's' : ''} d'ouverture de fenêtre en attente
              </div>
              <div className="text-[11px] text-amber-700 dark:text-amber-300">
                Des formateurs demandent une fenêtre de saisie exceptionnelle pour leur matière.
              </div>
            </div>
          </div>
          <div className="divide-y divide-amber-200 dark:divide-amber-800/40">
            {(demandes?.results || []).slice(0, 5).map(d => (
              <div key={d.id} className="px-4 py-2 flex items-center gap-3 hover:bg-amber-100/50 dark:hover:bg-amber-900/30">
                <UserIcon size={14} className="text-amber-600 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {d.formateur_nom} — <span className="font-mono text-xs">{d.classe}</span> · {d.matiere_libelle}
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                    {d.type_label} S{d.semestre} · {d.duree_jours_demandee} j · {new Date(d.date_demande).toLocaleDateString('fr-FR')}
                    {d.motif && ` — ${d.motif.slice(0, 80)}${d.motif.length > 80 ? '…' : ''}`}
                  </div>
                </div>
                <button onClick={() => setDetailDemande(d)}
                  className="px-2 py-1 text-[11px] rounded bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/40">
                  Traiter
                </button>
              </div>
            ))}
            {nbDemandesEnAttente > 5 && (
              <div className="px-4 py-1.5 text-[11px] text-amber-700 dark:text-amber-300 text-center">
                +{nbDemandesEnAttente - 5} autre(s)…
              </div>
            )}
          </div>
        </div>
      )}

      {!data?.results.length ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-10 text-center">
          <Calendar size={36} strokeWidth={1.3} className="mx-auto mb-3 text-gray-400 dark:text-gray-600" />
          <div className="text-gray-600 dark:text-gray-400 text-sm">Aucune fenêtre configurée.</div>
          <div className="text-xs text-gray-500 mt-1">
            Sans fenêtre active, les formateurs ne peuvent pas saisir.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {data.results.map(f => (
            <div key={f.id} className={`bg-white dark:bg-gray-800 border rounded-xl p-4 ${
              f.active_now
                ? 'border-green-300 dark:border-green-700'
                : 'border-gray-200 dark:border-gray-700'
            }`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    {f.libelle}
                    {f.active_now ? (
                      <span className="inline-flex items-center gap-1 text-[10px] bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 px-2 py-0.5 rounded">
                        <CheckCircle2 size={11} /> Active
                      </span>
                    ) : f.ouverte ? (
                      <span className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 px-2 py-0.5 rounded">
                        Programmée
                      </span>
                    ) : (
                      <span className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 px-2 py-0.5 rounded">
                        Suspendue
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {f.type_label} • Semestre {f.semestre}
                    {f.annee_libelle && ` • ${f.annee_libelle}`}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleToggle(f)}
                    className={`p-1.5 rounded text-xs ${
                      f.ouverte
                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                        : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                    }`}
                    title={f.ouverte ? 'Suspendre' : 'Reprendre'}>
                    {f.ouverte ? <Lock size={13} /> : <Unlock size={13} />}
                  </button>
                  <button onClick={() => { setEditing(f); setShowForm(true); }}
                    className="p-1.5 text-gray-500 hover:text-blue-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                    <Edit2 size={13} />
                  </button>
                  <button onClick={() => handleDelete(f.id)}
                    className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-gray-50 dark:bg-gray-900/50 rounded p-2">
                  <div className="text-[10px] uppercase font-semibold text-gray-500">Période</div>
                  <div className="text-sm text-gray-900 dark:text-white mt-0.5">
                    {new Date(f.date_debut).toLocaleDateString('fr-FR')}
                    {' → '}
                    {new Date(f.date_fin).toLocaleDateString('fr-FR')}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/50 rounded p-2">
                  <div className="text-[10px] uppercase font-semibold text-gray-500">Ciblage</div>
                  <div className="text-xs text-gray-700 dark:text-gray-300 mt-0.5">
                    {f.classes_ciblees.length === 0 && f.matieres_ciblees.length === 0
                      ? 'Toutes classes & matières'
                      : `${f.classes_ciblees.length || '∗'} classe(s) • ${f.matieres_ciblees.length || '∗'} matière(s)`}
                  </div>
                </div>
              </div>

              {(f.classes_ciblees.length > 0 || f.matieres_ciblees.length > 0) && (
                <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-2 text-xs">
                  {f.classes_ciblees.length > 0 && (
                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded p-2">
                      <div className="text-[10px] uppercase font-semibold text-gray-500 mb-1">
                        Classes libérées ({f.classes_ciblees.length})
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {f.classes_ciblees.map((c) => (
                          <span key={c}
                            className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 rounded font-mono text-[11px]">
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {f.matieres_ciblees.length > 0 && (
                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded p-2">
                      <div className="text-[10px] uppercase font-semibold text-gray-500 mb-1">
                        Matières libérées ({f.matieres_ciblees.length})
                      </div>
                      <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                        {f.matieres_ciblees.map((m) => (
                          <span key={m}
                            className="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 rounded text-[11px]">
                            {m}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Formateurs ciblés (fix 2026-09-01 : afficher les noms) */}
              {f.formateurs_cibles_ids && f.formateurs_cibles_ids.length > 0 && (
                <div className="mt-2 bg-blue-50 dark:bg-blue-900/20 rounded p-2 text-xs border border-blue-200 dark:border-blue-800/40">
                  <div className="text-[10px] uppercase font-semibold text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1">
                    <UserIcon size={9} /> Formateur(s) concerné(s) ({f.formateurs_cibles_ids.length})
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(f.formateurs_cibles_details && f.formateurs_cibles_details.length > 0
                      ? f.formateurs_cibles_details
                      : f.formateurs_cibles_ids.map(id => ({ id, nom_complet: `Utilisateur #${id}`, actif: true }))
                    ).map(u => (
                      <span key={u.id}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${
                          u.actif
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-900 dark:text-blue-200 border border-blue-300 dark:border-blue-700'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-500 border border-gray-300 dark:border-gray-600 line-through'
                        }`}
                        title={u.actif ? undefined : 'Utilisateur inactif ou supprimé'}
                      >
                        <UserIcon size={9} /> {u.nom_complet}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <ModalFenetre editing={editing} onClose={() => setShowForm(false)} />
      )}

      {/* Modal traitement demande (fix 2026-09-01) */}
      {detailDemande && (
        <ModalTraiterDemande
          demande={detailDemande}
          traitant={traitant}
          onClose={() => setDetailDemande(null)}
          onApprouver={handleApprouver}
          onRefuser={handleRefuser}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Modal : traiter une demande (approuver / refuser)
// ═════════════════════════════════════════════════════════════════════════
function ModalTraiterDemande({
  demande, traitant, onClose, onApprouver, onRefuser,
}: {
  demande: DemandeFenetre; traitant: boolean;
  onClose: () => void;
  onApprouver: (d: DemandeFenetre, duree: number) => void;
  onRefuser: (d: DemandeFenetre, reponse: string) => void;
}) {
  const [duree, setDuree] = useState(demande.duree_jours_demandee || 7);
  const [reponse, setReponse] = useState('');
  const [mode, setMode] = useState<'approuver' | 'refuser'>('approuver');

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-lg w-full">
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="font-bold text-gray-900 dark:text-white text-sm">Traiter la demande</h3>
          <button onClick={onClose} className="text-gray-500"><X size={18} /></button>
        </header>

        <div className="p-5 space-y-3">
          {/* Récap demande */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-xs space-y-1">
            <div><span className="text-gray-500">Formateur :</span> <strong>{demande.formateur_nom}</strong></div>
            <div><span className="text-gray-500">Classe :</span> <span className="font-mono">{demande.classe}</span></div>
            <div><span className="text-gray-500">Matière :</span> {demande.matiere_libelle}</div>
            <div><span className="text-gray-500">Type :</span> {demande.type_label} · Semestre {demande.semestre}</div>
            <div><span className="text-gray-500">Demandé :</span> {demande.duree_jours_demandee} jour(s)</div>
            <div><span className="text-gray-500">Motif :</span> <em>{demande.motif}</em></div>
          </div>

          {/* Toggle action */}
          <div className="flex gap-2">
            <button onClick={() => setMode('approuver')}
              className={`flex-1 px-3 py-2 text-sm rounded border ${mode === 'approuver'
                ? 'bg-green-600 border-green-600 text-white'
                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'}`}>
              <Check size={14} className="inline mr-1" /> Approuver
            </button>
            <button onClick={() => setMode('refuser')}
              className={`flex-1 px-3 py-2 text-sm rounded border ${mode === 'refuser'
                ? 'bg-red-600 border-red-600 text-white'
                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'}`}>
              <XCircle size={14} className="inline mr-1" /> Refuser
            </button>
          </div>

          {mode === 'approuver' ? (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Durée d'ouverture (jours) — 1 à 60
              </label>
              <input type="number" min={1} max={60} value={duree}
                onChange={(e) => setDuree(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
              <div className="text-[11px] text-gray-500 mt-1">
                Une fenêtre ciblée sur ce formateur + matière + classe sera créée immédiatement.
                Le formateur recevra une notification push.
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Motif du refus <span className="text-red-500">*</span>
              </label>
              <textarea value={reponse} onChange={(e) => setReponse(e.target.value)} rows={3}
                placeholder="Explication qui sera envoyée au formateur…"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded">
            Annuler
          </button>
          {mode === 'approuver' ? (
            <button onClick={() => onApprouver(demande, duree)} disabled={traitant}
              className="px-4 py-1.5 text-sm bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded inline-flex items-center gap-2">
              {traitant ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Approuver ({duree} j)
            </button>
          ) : (
            <button onClick={() => onRefuser(demande, reponse)} disabled={traitant || !reponse.trim()}
              className="px-4 py-1.5 text-sm bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded inline-flex items-center gap-2">
              {traitant ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
              Refuser
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

function ModalFenetre({ editing, onClose }: { editing: Fenetre | null; onClose: () => void }) {
  const [libelle, setLibelle] = useState(editing?.libelle || '');
  const [typeNotes, setTypeNotes] = useState(editing?.type_notes || 'devoirs');
  const [semestre, setSemestre] = useState(editing?.semestre || 1);
  const [annee, setAnnee] = useState(editing?.annee_libelle || '2025-2026');
  const [dateDebut, setDateDebut] = useState(editing?.date_debut || new Date().toISOString().slice(0, 10));
  const [dateFin, setDateFin] = useState(editing?.date_fin || new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));
  const [ouverte, setOuverte] = useState(editing?.ouverte ?? true);
  const [classesChoisies, setClassesChoisies] = useState<string[]>(editing?.classes_ciblees || []);
  const [matieresChoisies, setMatieresChoisies] = useState<string[]>(editing?.matieres_ciblees || []);
  const [formateursChoisis, setFormateursChoisis] = useState<number[]>(editing?.formateurs_cibles_ids || []);
  const [rechercheForm, setRechercheForm] = useState('');

  const { data: classesData } = useGetClassesQuery({ actif: true });
  const { data: matieresData } = useGetMatieresQuery({ actif: true });
  const { data: formateursData } = useListerFormateursQuery();
  // Fix 2026-09-08 : restreindre les matières à celles enseignées par les
  // formateurs sélectionnés (toggle activable par l'utilisateur).
  const [restreindreAuxMatieresFormateur, setRestreindreAuxMatieresFormateur] = useState(true);
  const { data: matieresFormateurData, isFetching: loadingMatFormateur } =
    useMatieresDesFormateursQuery(formateursChoisis, {
      skip: formateursChoisis.length === 0,
    });
  const matieresFormateurIds = useMemo(() => {
    const set = new Set<number>();
    (matieresFormateurData?.matieres || []).forEach(m => set.add(m.matiere_id));
    return set;
  }, [matieresFormateurData]);
  const formateursListe = useMemo(() => {
    const arr = formateursData?.results || [];
    if (!rechercheForm.trim()) return arr;
    const q = rechercheForm.toLowerCase();
    return arr.filter(f => f.nom_complet.toLowerCase().includes(q) || f.email.toLowerCase().includes(q));
  }, [formateursData, rechercheForm]);
  const classes = useMemo(() => (classesData || []) as any[], [classesData]);
  // Filtre semestre : la matière stocke un semestre GLOBAL (1..6) tandis que
  // `semestre` ici est le semestre LOCAL (1 ou 2). On les apparie :
  //   S1 (local) → semestres impairs (L1S1=1, L2S3=3, L3S5=5)
  //   S2 (local) → semestres pairs   (L1S2=2, L2S4=4, L3S6=6)
  const matieres = useMemo(() => {
    const arr = (matieresData || []) as any[];
    const semInt = Number(semestre);
    let filtered = arr.filter(m => {
      const semMat = Number(m.semestre);
      if (!semMat) return false;
      return semInt === 1 ? (semMat % 2 === 1) : (semMat % 2 === 0);
    });
    // Fix 2026-09-08 : restriction aux matières du formateur sélectionné
    if (restreindreAuxMatieresFormateur && formateursChoisis.length > 0 && matieresFormateurIds.size > 0) {
      filtered = filtered.filter(m => matieresFormateurIds.has(m.id));
    }
    return filtered.sort((a, b) =>
      (a.niveau || '').localeCompare(b.niveau || '') || a.nom_matiere.localeCompare(b.nom_matiere)
    );
  }, [matieresData, semestre, restreindreAuxMatieresFormateur, formateursChoisis, matieresFormateurIds]);

  const [creer, { isLoading: creating }] = useCreerFenetreMutation();
  const [update, { isLoading: updating }] = useUpdateFenetreMutation();

  const toggleClasse = (code: string) =>
    setClassesChoisies(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  const toggleMatiere = (id: string) =>
    setMatieresChoisies(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  const toggleFormateur = (id: number) =>
    setFormateursChoisis(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const body: any = {
      libelle, type_notes: typeNotes, semestre, annee_libelle: annee,
      date_debut: dateDebut, date_fin: dateFin, ouverte,
      classes_ciblees: classesChoisies,
      matieres_ciblees: matieresChoisies,
      formateurs_cibles_ids: formateursChoisis,
    };
    try {
      if (editing) await update({ id: editing.id, data: body }).unwrap();
      else         await creer(body).unwrap();
      onClose();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <header className="sticky top-0 bg-white dark:bg-gray-900 z-10 px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="font-bold text-gray-900 dark:text-white">
            {editing ? 'Modifier la fenêtre' : 'Nouvelle fenêtre de saisie'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-500"><X size={18} /></button>
        </header>

        <div className="p-5 space-y-4">
          <C label="Libellé" required>
            <input value={libelle} onChange={(e) => setLibelle(e.target.value)}
              className={I} required placeholder="Ex. Saisie devoirs S1 — Mars 2026" maxLength={200} />
          </C>

          <div className="grid grid-cols-2 gap-3">
            <C label="Type de notes">
              <select value={typeNotes} onChange={(e) => setTypeNotes(e.target.value)} className={I}>
                <option value="devoirs">Devoirs (D1/D2)</option>
                <option value="examens">Examens</option>
                <option value="rattrapage">Rattrapage</option>
              </select>
            </C>
            <C label="Semestre">
              <select value={semestre} onChange={(e) => setSemestre(Number(e.target.value))} className={I}>
                <option value={1}>Semestre 1</option>
                <option value={2}>Semestre 2</option>
              </select>
            </C>
          </div>

          <C label="Année académique">
            <input value={annee} onChange={(e) => setAnnee(e.target.value)} className={I} placeholder="2025-2026" />
          </C>

          <div className="grid grid-cols-2 gap-3">
            <C label="Date début" required>
              <input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} className={I} required />
            </C>
            <C label="Date fin" required>
              <input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} className={I} required />
            </C>
          </div>

          {/* Classes en multi-select */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Classes ciblées <span className="text-gray-500">(vide = toutes)</span>
              </label>
              <div className="flex gap-2 text-[10px]">
                <button type="button" onClick={() => setClassesChoisies(classes.map((c: any) => c.code))}
                  className="text-blue-600 dark:text-blue-400 hover:underline">Tout cocher</button>
                <button type="button" onClick={() => setClassesChoisies([])}
                  className="text-gray-500 hover:underline">Tout décocher</button>
              </div>
            </div>
            <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-gray-50 dark:bg-gray-800/50">
              <div className="flex flex-wrap gap-1.5">
                {classes.map((c: any) => {
                  const actif = classesChoisies.includes(c.code);
                  return (
                    <button key={c.code} type="button" onClick={() => toggleClasse(c.code)}
                      className={`px-2 py-1 text-[11px] font-medium rounded border inline-flex items-center gap-1 ${
                        actif
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600'
                      }`}>
                      {actif && <Check size={9} />} {c.code}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Matières en multi-select (filtrées par semestre) */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Matières ciblées <span className="text-gray-500">(vide = toutes du semestre {semestre})</span>
              </label>
              <div className="flex gap-2 text-[10px]">
                <button type="button" onClick={() => setMatieresChoisies(matieres.map((m: any) => m.code_matiere))}
                  className="text-blue-600 dark:text-blue-400 hover:underline">Tout cocher</button>
                <button type="button" onClick={() => setMatieresChoisies([])}
                  className="text-gray-500 hover:underline">Tout décocher</button>
              </div>
            </div>
            {/* Fix 2026-09-08 : toggle restreindre aux matières du formateur */}
            {formateursChoisis.length > 0 && (
              <label className="flex items-center gap-2 mb-1.5 p-2 rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={restreindreAuxMatieresFormateur}
                  onChange={(e) => setRestreindreAuxMatieresFormateur(e.target.checked)}
                  className="w-4 h-4 accent-blue-600"
                />
                <div className="flex-1 text-[11px]">
                  <div className="font-semibold text-blue-800 dark:text-blue-300">
                    Ne montrer que les matières du/des formateur(s) sélectionné(s)
                  </div>
                  <div className="text-blue-700 dark:text-blue-400 text-[10px]">
                    {loadingMatFormateur ? 'Chargement des attributions…' : (
                      matieresFormateurIds.size > 0
                        ? `${matieresFormateurIds.size} matière(s) attribuée(s) — ${matieres.length} visible(s) après filtre semestre`
                        : 'Aucune matière attribuée trouvée pour ces formateurs'
                    )}
                  </div>
                </div>
              </label>
            )}
            <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-gray-50 dark:bg-gray-800/50">
              {matieres.length === 0 ? (
                <div className="text-[11px] text-gray-500 py-2 text-center">
                  Aucune matière pour le semestre {semestre}.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {matieres.map((m: any) => {
                    const actif = matieresChoisies.includes(m.code_matiere);
                    return (
                      <button key={m.code_matiere} type="button" onClick={() => toggleMatiere(m.code_matiere)}
                        className={`px-2 py-1 text-[11px] rounded border inline-flex items-center gap-1.5 text-left ${
                          actif
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600'
                        }`}>
                        {actif && <Check size={9} className="flex-shrink-0" />}
                        <span className="truncate">
                          <span className={`text-[9px] ${actif ? 'opacity-80' : 'text-gray-500'}`}>[{m.niveau}]</span> {m.nom_matiere}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Formateurs ciblés (multi-select) — fix 2026-09-01 */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Formateurs ciblés <span className="text-gray-500">(vide = tous ceux concernés par les matières/classes)</span>
              </label>
              {formateursChoisis.length > 0 && (
                <button type="button" onClick={() => setFormateursChoisis([])}
                  className="text-[10px] text-gray-500 hover:underline">Tout décocher</button>
              )}
            </div>
            <input
              value={rechercheForm}
              onChange={(e) => setRechercheForm(e.target.value)}
              placeholder="Chercher un formateur (nom, email)…"
              className={`${I} mb-2`}
            />
            <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-gray-50 dark:bg-gray-800/50">
              {formateursListe.length === 0 ? (
                <div className="text-[11px] text-gray-500 py-2 text-center">
                  {rechercheForm.trim() ? 'Aucun formateur trouvé.' : 'Chargement…'}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {formateursListe.map(f => {
                    const actif = formateursChoisis.includes(f.id);
                    return (
                      <button key={f.id} type="button" onClick={() => toggleFormateur(f.id)}
                        className={`px-2 py-1 text-[11px] rounded border inline-flex items-center gap-1.5 text-left ${
                          actif
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600'
                        }`}>
                        {actif && <Check size={9} className="flex-shrink-0" />}
                        <span className="truncate">{f.nom_complet}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {formateursChoisis.length > 0 && (
              <div className="mt-1 text-[10px] text-blue-600 dark:text-blue-400">
                <UserIcon size={10} className="inline mr-1" />
                {formateursChoisis.length} formateur(s) ciblé(s) — une notification push leur sera envoyée.
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={ouverte} onChange={(e) => setOuverte(e.target.checked)} />
            <span className="text-sm text-gray-700 dark:text-gray-200">Fenêtre activée</span>
            <span className="text-xs text-gray-500 ml-2">(décocher = suspendre sans supprimer)</span>
          </label>
        </div>

        <footer className="sticky bottom-0 bg-white dark:bg-gray-900 px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded">
            Annuler
          </button>
          <button type="submit" disabled={creating || updating}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded inline-flex items-center gap-2">
            {(creating || updating) ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {editing ? 'Enregistrer' : 'Créer'}
          </button>
        </footer>
      </form>
    </div>
  );
}

const I = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100';
function C({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
    </div>
  );
}
